import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import { CATALOGUE_PLATFORMS_LOGO } from "../../../client-config";
import { extractInitials, lS } from "../../../atlas-utils";
import { SelectFilterCustom } from "../../../components/_commons/SelectFilterCustom";
import { fetchBizPlatforms, fetchBrands, fetchStores, fetchStoresDebounced } from "../../../actions/actions";
import {
	fetchDashboardMetrics,
	fetchDebouncedPersicopeEnabledCities,
	fetchItemMetrics,
	fetchPeriscopeEnabledBrands,
	fetchPeriscopeEnabledCities,
	fetchPeriscopeEnabledLocations,
	fetchStoreMetrics,
	fetchStores as fetchStoresList
} from "../../../actions/periscope";
import uniqBy from "lodash/uniqBy";
import { getItemsByPage, periscopeFilterEvent } from "../../../helpers/periscope";
import { store } from "../../../store/configureStore";
import { ActionTypes } from "../../../actions/_types";
import SatismeterService from "../../../services/SatismeterService";
import { SelectFilterCustomGroup } from "../../../components/_commons/SelectFilterCustomGroup";

const HomeList = ({
	configItems,
	login,
	isMultibrandEnabled,
	periscopeDataResponse,
	periscopeFilters,
	periscopeCountryWiseSupportedPlatforms
}) => {
	const country = (login?.loggedInbizDetail?.country || lS.get("auth")?.biz?.country).toLowerCase();
	const bizPlatforms = configItems?.bizPlatforms?.items.filter((platform) => {
		if (platform.platformName == "All Platforms") {
			return true;
		}
		return (periscopeCountryWiseSupportedPlatforms?.[country] ||
			lS.get("periscopeCountryWisePlatforms")?.[country])?.[platform.platformName.toLowerCase()]?.enabled;
	});
	const bizPlatformsLoading = configItems?.bizPlatforms?.isLoading;
	const brands = configItems?.brands?.items;
	const stores = configItems?.stores?.items;

	const cities = periscopeFilters?.cities;
	const periscopeBrandIds = periscopeFilters?.brands;
	const periscopeLocationIds = periscopeFilters?.locations;

	const [filteredBrands, setFilteredBrands] = useState([]);

	useEffect(() => {
		if (!isMultibrandEnabled || !brands || !periscopeBrandIds) return;

		const filteredBrands = brands.filter((brand) => {
			return periscopeBrandIds.includes(String(brand?.id)) || brand?.id === "all";
		});
		setFilteredBrands(filteredBrands);
	}, [isMultibrandEnabled, brands, periscopeBrandIds]);

	const [selectedPlatform, setSelectedPlatform] = useState(periscopeFilters?.selectedPlatform);

	const [selectedBrand, setSelectedBrand] = useState(isMultibrandEnabled ? periscopeFilters.selectedBrand : null);

	const [selectedCity, setSelectedCity] = useState({
		value: "all",
		valueForDisplay: "All Cities",
		__typename: "CityType"
	});
	const [locationSelected, setLocationSelected] = useState({ id: "all", name: "All Locations" });

	const handlePlatformsLabelOption = (plf) => {
		return (
			<React.Fragment>
				<div className="logo">
					<img
						src={
							CATALOGUE_PLATFORMS_LOGO[plf.platformName.toLowerCase()] ||
							"/assets/icons/icons8-globe-40.png"
						}
						alt=""
					/>
				</div>
				<div title={plf.platformName}>
					{plf.platformName && plf.platformName.length > 25
						? plf.platformName.slice(0, 25) + "..."
						: plf.platformName}
				</div>
			</React.Fragment>
		);
	};

	const handleFiltersLabelOption = (filter) => {
		return (
			<React.Fragment>
				<div className={"logo " + filter.color}>
					{filter.image ? <img src={filter.image} alt="" /> : extractInitials(filter?.name?.split(" "))}
				</div>
				<div className="brand-title" title={filter.name}>
					{filter.name && filter.name.length > 18 ? filter.name.slice(0, 18) + "..." : filter.name}
				</div>
			</React.Fragment>
		);
	};

	const handlePlatform = (value) => {
		periscopeFilterEvent("platform");
		setSelectedPlatform(value);
		store.dispatch({
			type: ActionTypes.GET_PERISCOPE_FILTERS,
			payload: {
				selectedPlatform: { id: value.id, platformName: value.platformName }
			}
		});
		SatismeterService.satismeterEvent("periscopeFilterApplied");
	};

	const handleBrand = (brand) => {
		periscopeFilterEvent("brand");
		setSelectedBrand(brand);
		store.dispatch({
			type: ActionTypes.GET_PERISCOPE_FILTERS,
			payload: {
				selectedBrand: brand
			}
		});
		SatismeterService.satismeterEvent("periscopeFilterApplied");
		handleReset();
	};

	const handleLocation = useCallback((value) => {
		periscopeFilterEvent("location");
		setLocationSelected(value);
		SatismeterService.satismeterEvent("periscopeFilterApplied");
	}, []);

	const handleCity = (city) => {
		periscopeFilterEvent("city");
		setSelectedCity(city);
		SatismeterService.satismeterEvent("periscopeFilterApplied");
	};

	const fetchStoreData = useCallback(
		async (offsetZero = false) => {
			try {
				store.dispatch({
					type: ActionTypes.PERISCOPE_FILTER_METRIC_CHANGE,
					payload: {
						metric: "locationsList",
						data: { isLoading: true }
					}
				});
				if (!Array.isArray(periscopeLocationIds) || periscopeLocationIds.length === 0) {
					store.dispatch({
						type: ActionTypes.PERISCOPE_FILTER_METRIC_CHANGE,
						payload: {
							metric: "locationsList",
							data: { isLoading: false }
						}
					});
					return;
				}

				const limit = periscopeFilters?.locationsList?.limit || 50;
				const currentItems = periscopeFilters?.locationsList?.items || [];
				const offset = offsetZero ? 0 : currentItems.length ? currentItems.length - 1 : 0;

				let ids = getItemsByPage(periscopeLocationIds, offset, limit);

				const respItems = await fetchStoresList("", limit, 0, null, offset == 0 ? true : false, ids);

				store.dispatch({
					type: ActionTypes.PERISCOPE_FILTER_METRIC_CHANGE,
					payload: {
						metric: "locationsList",
						data: {
							items:
								offset == 0
									? uniqBy([...(respItems?.data?.stores?.objects || [])], "id")
									: uniqBy(
											[
												...(periscopeFilters?.locationsList?.items || []),
												...(respItems?.data?.stores?.objects || [])
											],
											"id"
										),

							offset: respItems?.data?.stores?.offset || 0,
							limit,
							count: (periscopeLocationIds && periscopeLocationIds.length) || 0,
							isLoading: false
						}
					}
				});
			} catch (err) {
				console.log("Error fetching store list", err);
				store.dispatch({
					type: ActionTypes.PERISCOPE_FILTER_METRIC_CHANGE,
					payload: {
						metric: "locationsList",
						data: { isLoading: false }
					}
				});
			}
		},
		[periscopeFilters?.locationsList, periscopeLocationIds]
	);

	useEffect(() => {
		if ((periscopeLocationIds || []).length) {
			fetchStoreData(true);
		}
	}, [(periscopeLocationIds || []).length, periscopeFilters?.locationsList?.limit]);

	useEffect(() => {
		fetchBizPlatforms(false, false, true);
		fetchBrands("", true);
		fetchStores("", 50, null, true);
		fetchPeriscopeEnabledCities(String(login?.loggedInbizDetail?.id), true);
		fetchPeriscopeEnabledBrands(String(login?.loggedInbizDetail?.id));
		fetchPeriscopeEnabledLocations(String(login?.loggedInbizDetail?.id));
	}, []);

	const fetchDashboardData = async (variables, periscopeDataFetch = false) => {
		fetchDashboardMetrics(variables, periscopeDataFetch);
		fetchItemMetrics(variables, periscopeDataFetch);
		fetchStoreMetrics(variables, periscopeDataFetch);
	};
	const getVariables = () => {
		const platformNames = [
			selectedPlatform?.id === "all" ? selectedPlatform.id : selectedPlatform.platformName?.toLowerCase()
		];
		return {
			bizId: String(login?.loggedInbizDetail?.id),
			brand: isMultibrandEnabled ? String(selectedBrand?.id || "all") : null,
			location: String(locationSelected?.id),
			city: String(selectedCity?.value),
			platform: String(selectedPlatform?.id),
			platforms: platformNames
		};
	};
	useEffect(() => {
		let variables = { ...getVariables() };
		fetchDashboardData(variables);
	}, [selectedBrand?.id, selectedCity?.value, selectedPlatform?.id, locationSelected?.id]);

	const periscopeDataFetch = periscopeDataResponse?.periscopeDataFetch;
	useEffect(() => {
		if (periscopeDataFetch) {
			let variables = { ...getVariables() };
			fetchDashboardData(variables, periscopeDataFetch);
			fetchItemMetrics(variables, periscopeDataFetch);
			fetchStoreMetrics(variables, periscopeDataFetch);
		}
	}, [periscopeDataFetch]);

	const handleCitySearch = (text) => {
		let includeAll = true;
		if (text) {
			includeAll = false;
		}
		fetchDebouncedPersicopeEnabledCities(String(login?.loggedInbizDetail?.id), includeAll, text);
	};

	const handleReset = () => {
		handleCity({
			value: "all",
			valueForDisplay: "All Cities",
			__typename: "CityType"
		});
		handlePlatform({ id: "all", platformName: "All Platforms" });
		store.dispatch({
			type: ActionTypes.GET_PERISCOPE_FILTERS,
			selectedPlatform: { id: "all", platformName: "All Platforms" }
		});
		handleLocation({ id: "all", name: "All Locations" });
	};

	const handleFilterState = (isOpen, field) => {
		let filters = {
			locations: () => fetchStoreData(true),
			cities: () => fetchPeriscopeEnabledCities(String(login?.loggedInbizDetail?.id), true)
		};
		if (isOpen) {
			const filterAction = filters[field];
			filterAction();
		} else {
			if (field == "locations") {
				store.dispatch({
					type: ActionTypes.PERISCOPE_FILTER_METRIC_CHANGE,
					payload: {
						metric: "locationsList",
						data: {
							text: ""
						}
					}
				});
			}
		}
	};

	const handleFetchOption = useCallback(
		(entity, offsetZero = false) => {
			if (periscopeLocationIds && periscopeLocationIds.length > 0) {
				fetchStoreData(offsetZero);
			}
		},
		[periscopeLocationIds, fetchStoreData]
	);

	const [locationSearchText, setLocationSearchText] = useState(false); //Boolean
	const handleLocationTextSearch = (text) => {
		if (text) {
			setLocationSearchText(true);
		} else {
			setLocationSearchText(false);
		}
		store.dispatch({
			type: ActionTypes.PERISCOPE_FILTER_METRIC_CHANGE,
			payload: {
				metric: "locationsList",
				data: {
					text: text || ""
				}
			}
		});
		fetchStoresDebounced(text, 50, null, false);
	};

	const locationSearchedText = periscopeFilters?.locationsList?.text || "";
	const allLocations = useMemo(() => {
		return (stores || []).filter((store) => {
			if (!Array.isArray(periscopeLocationIds) || periscopeLocationIds.length === 0) {
				return false;
			}

			if (!store) return false;
			const isMatchingName = store?.name
				? store.name.toLowerCase()?.includes((locationSearchedText || "").toLowerCase())
				: true;
			const isValidLocation = periscopeLocationIds.includes(String(store.id));
			return isValidLocation && isMatchingName;
		});
	}, [stores, periscopeLocationIds, locationSearchedText]);
	const optionValues = useMemo(() => {
		return [
			{
				options: locationSearchText ? allLocations : periscopeFilters?.locationsList?.items || [],
				isLoading: locationSearchText
					? configItems?.stores?.isLoading
					: periscopeFilters?.locationsList?.isLoading || false,
				showSelectAllOption: true,
				field: "locations",
				labelKey: "name",
				valueKey: "id",
				ellipsizedLength: 27,
				iconKey: "image",
				countDisplayText: "location",
				viewAllBtn: true,
				setOffsetToZero: true,
				viewMore:
					periscopeFilters?.locationsList &&
					periscopeFilters?.locationsList?.items &&
					(periscopeFilters?.locationsList?.items || []).length < periscopeFilters?.locationsList?.count + 1,
				isSearchExternal: true,
				handleFetchViewOptions: handleFetchOption,
				isSearchable: true,
				placeholder: "All Locations",
				setFilter: (f, value) => handleLocation(value),
				currValue: locationSelected
			}
		];
	}, [
		locationSearchText,
		allLocations,
		periscopeFilters?.locationsList,
		configItems?.stores?.isLoading,
		handleFetchOption,
		handleLocation,
		locationSelected
	]);

	const renderCustomLabelLocations = useCallback((option) => {
		return <div>{option[0]["currValue"]["name"]}</div>;
	}, []);

	return (
		<>
			<div className={"filter-div brand-state-filters"}>
				{isMultibrandEnabled && (
					<SelectFilterCustom
						options={filteredBrands}
						field="brands"
						currValue={selectedBrand}
						setFilter={(f, value) => handleBrand(value)}
						labelKey={"name"}
						valueKey={"id"}
						isSearchable={true}
						customLabel={true}
						customOptions={true}
						renderLabel={handleFiltersLabelOption}
						renderOptions={handleFiltersLabelOption}
						placeholder="Select brand"
					/>
				)}

				<SelectFilterCustomGroup
					renderCustomLabel={renderCustomLabelLocations}
					customLabel
					groupedOptions={optionValues}
					handleSelectFilterState={handleFilterState}
					handleSearch={handleLocationTextSearch}
				/>

				<SelectFilterCustom
					options={cities}
					field="cities"
					currValue={selectedCity || cities[0]}
					setFilter={(f, value) => {
						handleCity(value);
					}}
					labelKey={"valueForDisplay"}
					valueKey={"value"}
					isSearchable={true}
					placeholder="All Cities"
					handleSearch={handleCitySearch}
					handleSelectFilterState={handleFilterState}
					classes="city-filter"
				/>

				<SelectFilterCustom
					options={bizPlatforms}
					isLoading={bizPlatformsLoading}
					field="platforms"
					currValue={selectedPlatform || null}
					setFilter={(f, value) => handlePlatform(value)}
					labelKey={"platformName"}
					valueKey={"id"}
					isSearchable={false}
					customLabel={true}
					customOptions={true}
					renderLabel={handlePlatformsLabelOption}
					renderOptions={handlePlatformsLabelOption}
					placeholder="Select platform"
				/>
			</div>
		</>
	);
};

const mapStateToProps = (state) => {
	return {
		configItems: state.configItems,
		login: state.login,
		isMultibrandEnabled: state.login?.loggedInbizDetail?.isMultibrandEnabled,
		periscopeDataResponse: state.periscopeDataResponse,
		periscopeCountryWiseSupportedPlatforms: state.periscopeCountryWiseSupportedPlatforms,
		periscopeFilters: state.periscopeFilters
	};
};

export default connect(mapStateToProps)(HomeList);
